import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toolboxUpdateNoticeSchema, type ToolboxBridge, type ToolboxUpdateNotice } from "@ttc-spectree/toolbox-contract";
import { useToolboxBridge } from "./bridge";

/**
 * The unsigned-pilot update notice from SpecTree Toolbox (epic PI-SPEC-SW-E288,
 * feature PI-SPEC-SW-1468, UX-PM-41). The app reports it through the optional
 * `bridge.app.getUpdateNotice` / `onUpdateNotice`; the band at the top of
 * every Toolbox page and the title bar's "Update ready" chip read it from one
 * shared query, so both always agree. A plain browser (no bridge), an older
 * app without the methods, or a malformed notice give null: no band, no chip.
 */

export const updateNoticeKey = ["toolbox", "update-notice"] as const;

/** A notice the page may show, or null. Pure; exported for tests. */
export function parseUpdateNotice(value: unknown): ToolboxUpdateNotice | null {
  const parsed = toolboxUpdateNoticeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function readsNotice(bridge: ToolboxBridge | null): bridge is ToolboxBridge & {
  app: ToolboxBridge["app"] & { getUpdateNotice: NonNullable<ToolboxBridge["app"]["getUpdateNotice"]> };
} {
  return typeof bridge?.app.getUpdateNotice === "function";
}

/**
 * How many notices the app has pushed. A `getUpdateNotice()` read that was
 * started before a push and answers after it is older than the pushed
 * notice, so it must not replace it (the band would show a stale state).
 */
let pushedNotices = 0;

/** The current update notice inside SpecTree Toolbox, kept current by the app's events. */
export function useUpdateNotice(): ToolboxUpdateNotice | null {
  const bridge = useToolboxBridge();
  const queryClient = useQueryClient();
  const enabled = readsNotice(bridge);

  const { data } = useQuery({
    queryKey: updateNoticeKey,
    queryFn: async (): Promise<ToolboxUpdateNotice | null> => {
      if (!readsNotice(bridge)) return null;
      const pushesBefore = pushedNotices;
      const read = parseUpdateNotice(await bridge.app.getUpdateNotice());
      // A notice pushed while this read was out is newer: keep it.
      if (pushedNotices !== pushesBefore) return queryClient.getQueryData<ToolboxUpdateNotice | null>(updateNoticeKey) ?? null;
      return read;
    },
    enabled,
    // The app pushes every change; never refetch on focus or a timer.
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    const app = bridge?.app;
    if (typeof app?.onUpdateNotice !== "function") return;
    return app.onUpdateNotice((notice) => {
      pushedNotices += 1;
      queryClient.setQueryData(updateNoticeKey, parseUpdateNotice(notice));
    });
  }, [bridge, queryClient]);

  return enabled ? (data ?? null) : null;
}

/** True when the title bar shows "Update ready": a download exists that this app is not running yet. */
export function updateReady(notice: ToolboxUpdateNotice | null): boolean {
  return notice !== null && notice.state !== "none" && notice.latestVersion !== undefined;
}
